import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import '@/styles/SipaStyles.scss';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

// Score a single control as a percentage [0-100] or null.
// Mirrors the capture page's scoreControl logic for full consistency.
const scoreControlPct = (ctrl, value) => {
  if (!value || value === 'NA') return null;
  if (ctrl.ExcludeFromScore || ctrl.excludeFromScore) return null;

  const rawMax = ctrl.MaxScore ?? ctrl.maxScore;
  const max = rawMax && rawMax > 0 ? rawMax : 100;
  const ctrlType = ctrl.Type || ctrl.type;

  if (ctrlType === 'Checkbox') {
    return value.toLowerCase() === 'true' ? 100 : 0;
  }
  if (ctrlType === 'Number') {
    const num = parseFloat(value);
    if (isNaN(num)) return 0;
    return Math.round(Math.max(0, Math.min(100, (num / max) * 100)) * 100) / 100;
  }
  if (ctrlType === 'Dropdown' || ctrlType === 'RadioGroup') {
    const options = (ctrl.Options || ctrl.options || []).filter(o => o.Active !== false && o.active !== false);
    if (!options.length) return null;

    // Filter out NA options for scoring
    const activeOptions = options.filter(o => {
      const optionValue = (o.Value ?? o.value ?? '').toString().toUpperCase();
      return optionValue !== 'NA';
    });
    if (!activeOptions.length) return null;

    // When every active option value is numeric, use the unified value-based
    // scoring pipeline so the list page matches capture-page/backend scoring.
    const numericOptionValues = activeOptions.map(option => {
      const optionValue = option.Value ?? option.value;
      const parsedValue = parseFloat(optionValue);
      return Number.isNaN(parsedValue) ? null : parsedValue;
    });

    const allOptionValuesAreNumeric = numericOptionValues.every(optionValue => optionValue !== null);
    if (allOptionValuesAreNumeric) {
      const selectedNumericValue = parseFloat(value);
      const maxNumericValue = Math.max(...numericOptionValues);

      if (Number.isNaN(selectedNumericValue) || !Number.isFinite(maxNumericValue) || maxNumericValue <= 0) {
        return 0;
      }

      return Math.round(
        Math.max(0, Math.min(100, (selectedNumericValue / maxNumericValue) * 100)) * 100
      ) / 100;
    }

    // Fall back to the existing position-based scoring for non-numeric option sets.
    const ordered = [...options].sort((a, b) => (a.SortOrder ?? a.sortOrder ?? 0) - (b.SortOrder ?? b.sortOrder ?? 0));
    const selectedIndex = ordered.findIndex(o => (o.Value ?? o.value) === value);
    if (selectedIndex === -1) return null;
    if (ordered.length === 1) return 100;
    return Math.round((1 - selectedIndex / (ordered.length - 1)) * 100 * 100) / 100;
  }
  return null; // Non-scoring types
};

// Compute category & overall scores from a structure object.
// Uses section-weighted averages of per-control percentages, matching
// the capture page scoring pipeline exactly.
const computeScoresFromStructure = (structure) => {
  const categories = structure?.Structure || structure?.structure;
  if (!structure || !categories) return { ga: null, oc: null, total: null, completionPct: 0 };

  // Per-category and overall accumulators: Σ(sectionAvg × sectionWeight) / Σ(sectionWeight)
  let gaNumerator = 0, gaWeight = 0;
  let ocNumerator = 0, ocWeight = 0;
  let allNumerator = 0, allWeight = 0;

  // Completion tracking: count scorable controls with/without a value
  let totalScorable = 0, filledScorable = 0;

  categories.forEach(cat => {
    const categoryKey = (cat.Category || cat.category || cat.CategoryName || cat.categoryName || cat.Name || cat.name || '').toString().toLowerCase();
    const isGA = categoryKey.includes('general');
    const isOC = categoryKey.includes('operations') || categoryKey.includes('compliance');

    (cat.Sections || cat.sections || []).forEach(sec => {
      const weight = sec.Weight ?? sec.weight ?? 0;
      if (weight <= 0) return;

      // Check if section is fully NA (every subsection has at least one NA control)
      const subs = sec.SubSections || sec.subSections || [];
      const isFullyNA = subs.length > 0 && subs.every(sub =>
        (sub.Controls || sub.controls || []).some(c => {
          const v = (c.Value ?? c.value ?? '').toString();
          return v === 'NA';
        })
      );
      if (isFullyNA) return;

      // Collect control percentages (skip subsections with any NA control)
      const pcts = [];
      subs.forEach(sub => {
        const ctrls = sub.Controls || sub.controls || [];
        const hasNA = ctrls.some(c => (c.Value ?? c.value ?? '').toString() === 'NA');
        if (hasNA) return;

        ctrls.forEach(ctrl => {
          const ctrlType = ctrl.Type || ctrl.type;
          const excluded = ctrl.ExcludeFromScore || ctrl.excludeFromScore;
          // Track completion for scorable control types
          if (!excluded && (ctrlType === 'Checkbox' || ctrlType === 'Number' || ctrlType === 'Dropdown' || ctrlType === 'RadioGroup')) {
            totalScorable++;
            const val = (ctrl.Value ?? ctrl.value ?? '').toString();
            if (val && val !== '') filledScorable++;
          }
          const val = (ctrl.Value ?? ctrl.value ?? '').toString();
          const p = scoreControlPct(ctrl, val);
          if (p !== null) pcts.push(p);
        });
      });

      if (!pcts.length) return;
      const sectionAvg = pcts.reduce((a, b) => a + b, 0) / pcts.length;

      allNumerator += sectionAvg * weight;
      allWeight += weight;
      if (isGA) { gaNumerator += sectionAvg * weight; gaWeight += weight; }
      if (isOC) { ocNumerator += sectionAvg * weight; ocWeight += weight; }
    });
  });

  const calc = (num, w) => w > 0 ? Math.round((num / w) * 100) / 100 : null;
  const completionPct = totalScorable > 0 ? Math.round((filledScorable / totalScorable) * 100) : 0;
  return { ga: calc(gaNumerator, gaWeight), oc: calc(ocNumerator, ocWeight), total: calc(allNumerator, allWeight), completionPct };
};

const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const SipaChart = ({ chartData, currentYear, previousYear, isOverall }) => {
  if (!chartData || chartData.length === 0) return null;
  return (
    <div className="dashboard-card" style={{padding:'12px 16px', marginTop:16}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12}}>
        <h3 style={{margin:0, fontSize:'0.95rem'}}>{isOverall ? 'Overall Cumulative Average Total Score (All Properties, Trailing 12 Months)' : 'Cumulative Average Total Score (Trailing 12 Months)'}</h3>
        <div style={{display:'flex', gap:12, fontSize:'0.7rem'}}>
          <div style={{display:'flex', alignItems:'center', gap:4}}><span style={{width:14, height:4, background:'#2563eb', display:'inline-block'}}></span>{currentYear}</div>
          <div style={{display:'flex', alignItems:'center', gap:4}}><span style={{width:14, height:4, background:'#f97316', display:'inline-block'}}></span>{previousYear}</div>
        </div>
      </div>
      <div style={{width:'100%', height:260}}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top:20, right:30, left:10, bottom:5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="monthLabel" tick={{fontSize:11}} />
            <YAxis domain={[0, 100]} tick={{fontSize:11}} />
            <Tooltip formatter={(val)=> val==null? '-' : `${val}%`} labelFormatter={(l)=> `Month: ${l}`} />
            <Legend wrapperStyle={{fontSize:'0.7rem'}} />
            <Line type="monotone" dataKey="previous" name={`${previousYear} Avg %`} stroke="#f97316" strokeWidth={2} dot={{r:3}} connectNulls={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="current" name={`${currentYear} Avg %`} stroke="#2563eb" strokeWidth={3} dot={{r:4}} connectNulls={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{fontSize:'0.65rem', color:'#555', marginTop:4}}>
        {isOverall ? 'Overall averages aggregate all property reports.' : 'Cumulative average for selected property.'}
      </div>
    </div>
  );
};

const SipaReportListPage = () => {
  const { apiFetch, user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState(''); // blank => all properties
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scoreMap, setScoreMap] = useState({});
  // Date filter (only when property not selected)
  const [filterMonth, setFilterMonth] = useState(''); // 1-12 string
  const [filterYear, setFilterYear] = useState(''); // year string
  // Start period picker (when property selected but no reports exist yet)
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  // Workflow permissions (drives Change Period visibility)
  const [canOverrideValidation, setCanOverrideValidation] = useState(false);
  // Change Period modal state
  const [changePeriodReport, setChangePeriodReport] = useState(null);
  const [changePeriodMonth, setChangePeriodMonth] = useState('');
  const [changePeriodYear, setChangePeriodYear] = useState('');
  const [changePeriodLoading, setChangePeriodLoading] = useState(false);
  const [changePeriodError, setChangePeriodError] = useState('');

  const propertyNameMap = properties.reduce((acc,p)=>{ const id = p.propertyID || p.PropertyID; const name = p.propertyName || p.PropertyName; if(id) acc[id]=name; return acc; },{});

  const formatPeriod = (year, month) => {
    try { return new Date(year, month-1, 1).toLocaleDateString(undefined,{ month:'long', year:'numeric'}); }
    catch (catchError) {
        Sentry.captureException(catchError);
        return `${year}-${String(month).padStart(2,'0')}`;
    }
  };

  const submissionStatus = (r) => {
    if(r.headSigned) return `${r.finalSignerRoleName || 'Final'} Approved`;
    if(r.romSigned) return `${r.primarySignerRoleName || 'Primary'} Submitted`;
    const scores = scoreMap[r.id];
    if (scores && scores.completionPct != null) {
      if (scores.completionPct === 0) return 'Not Started';
      return `${scores.completionPct}% complete`;
    }
    return 'Not Started';
  };

  const search = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if(propertyId) qs.append('propertyId', propertyId);
      // Year/month filtering is handled client-side by tableReports useMemo;
      // always fetch all data so the chart has the full trailing 12-month window.
      const res = await apiFetch(`/api/sipa/reports/search?${qs.toString()}`);
      const data = await res.json(); const list = Array.isArray(data)? data : []; setReports(list); return list;
    } finally { setLoading(false); }
  };

  const openReport = (report) => { navigate(`/sipa/report-capture?id=${report.id}`); };

  useEffect(()=>{
    let cancelled = false;
    const loadScores = async () => {
      if(!reports.length) { setScoreMap({}); return; }
      const entries = await Promise.all(reports.map(async r => {
        try {
          const res = await apiFetch(`/api/sipa/reports/structure/${r.id}`);
          if(!res.ok) return [r.id,{ ga:null, oc:null, total:null }];
          const structure = await res.json();
          // Prefer server-computed scores (consistent with Compare and MonthlyOverview)
          const serverGA = structure.ScoreGeneralAppearance ?? structure.scoreGeneralAppearance;
          const serverOC = structure.ScoreOperationsCompliance ?? structure.scoreOperationsCompliance;
          const serverTotal = structure.TotalScore ?? structure.totalScore;
          if (serverTotal != null) {
            // Compute completionPct from the structure even when the server
            // provides the scores — without it submissionStatus always shows
            // "Not Started" because completionPct would be undefined.
            const { completionPct } = computeScoresFromStructure(structure);
            return [r.id, { ga: serverGA ?? null, oc: serverOC ?? null, total: serverTotal, completionPct }];
          }
          // Fallback to client-side computation if server scores unavailable
          const scores = computeScoresFromStructure(structure);
          return [r.id, scores];
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            return [r.id,{ ga:null, oc:null, total:null }];
        }
      }));
      if(!cancelled){ const map = {}; entries.forEach(([id,val])=>{ map[id]=val; }); setScoreMap(map); }
    };
    loadScores();
    return ()=>{ cancelled = true; };
  },[reports, apiFetch]);

  useEffect(()=>{
    const loadProps = async () => {
      const resolvedUserId = user?.userID ?? user?.UserID;
      if (!resolvedUserId) return;
      try {
        const res = await apiFetch(`/api/Permissions/my-accessible-properties/${resolvedUserId}`);
        const data = await res.json();
        if (data.success && data.data) {
          // data.data.properties contains only the properties this user may access
          const props = [...(data.data.properties || [])].sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || ''));
          setProperties(props);
        }
      } catch (error) { 
          Sentry.captureException(error);
        // Property loading is non-critical for page functionality
        // Use warn level since users may still be able to use the page
        console.warn('Error loading properties:', error);
      }
    };
      loadProps();
  }, [user, apiFetch]);

  // Fetch SIPA workflow permissions for the current user
  useEffect(() => {
    const loadPerms = async () => {
      try {
        const res = await apiFetch('/api/sipa/workflow-config/user-permissions');
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            setCanOverrideValidation(!!data.data.canOverrideValidation);
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        console.warn('Error loading SIPA workflow permissions:', error);
      }
    };
    loadPerms();
  }, [apiFetch]);

  // Trigger search when property changes (year/month filtering is client-side)
  useEffect(() => {
    // Defer to avoid synchronous setState-in-effect lint warning.
    const id = setTimeout(() => { search().catch(() => {}); }, 0);
    return () => clearTimeout(id);
  }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset date filter and start period when property changes
  useEffect(() => {
    if (propertyId) {
      // Defer setState calls to avoid synchronous setState-in-effect lint warning
      setTimeout(() => {
        setFilterMonth('');
        setFilterYear('');
        setStartMonth('');
        setStartYear('');
      }, 0);
    }
  }, [propertyId]);

  // Whether the selected property has no reports yet (drives start period picker visibility)
  const propertyHasNoReports = propertyId && !loading && reports.filter(r => r.propertyId === parseInt(propertyId)).length === 0;

  // Build start year options: current year and previous year
  const startYearOptions = useMemo(() => {
    const now = new Date();
    const cur = now.getFullYear();
    return [cur, cur - 1];
  }, []);

  const openOrCreateCurrent = async () => {
    if(!propertyId) return;
    setLoading(true);
    try {
      const now = new Date();
      const yearNow = now.getFullYear();
      const monthNow = now.getMonth()+1;
      const pid = parseInt(propertyId);
      let currentList = await search();
      const propReports = currentList.filter(r => r.propertyId === pid);

      // If there is an existing unsigned report, open it directly
      const unsigned = propReports.find(r => !r.headSigned);
      if(unsigned){ navigate(`/sipa/report-capture?id=${unsigned.id}`); return; }

      // Determine the next sequential month after the latest report
      let targetYear, targetMonth;
      if(propReports.length){
        // Reports are ordered descending by period from the API
        const latest = propReports[0];
        const nextDate = new Date(latest.year, latest.month, 1); // month is 0-indexed, so latest.month (1-based) gives next month
        targetYear = nextDate.getFullYear();
        targetMonth = nextDate.getMonth() + 1;
      } else {
        // No reports yet — use the start period the user selected
        targetYear = parseInt(startYear);
        targetMonth = parseInt(startMonth);
      }

      // Do not allow creating reports for future months
      if(targetYear > yearNow || (targetYear === yearNow && targetMonth > monthNow)){
        alert(`All reports are up to date. The next period (${monthNames[targetMonth-1]} ${targetYear}) cannot be started until that month begins.`);
        return;
      }

      // Create the next sequential report
      const ensureRes = await apiFetch(`/api/sipa/reports/ensure?propertyId=${propertyId}&year=${targetYear}&month=${targetMonth}`, { method:'POST' });
      if(!ensureRes.ok){
        const err = await ensureRes.text();
        alert(`Could not create report: ${err}`);
        return;
      }
      currentList = await search();
      const created = currentList.find(r => r.year === targetYear && r.month === targetMonth && r.propertyId === pid);
      if(created) navigate(`/sipa/report-capture?id=${created.id}`); else alert('Unable to locate or create report');
    } finally { setLoading(false); }
    };

    // ---------------- Table Data Derivation ----------------
    const tableReports = useMemo(() => {
        if (propertyId) {
            // last 12 reports for property (descending already from API)
            return reports.slice(0, 12);
        }
        // No property selected: decide month/year
        if (!reports.length) return [];
        // Reports are ordered descending by year then month from API
        const mostRecent = reports[0];
        let targetYear = mostRecent.year;
        let targetMonth = mostRecent.month;
        if (filterYear && filterMonth) { targetYear = parseInt(filterYear); targetMonth = parseInt(filterMonth); }
        else if (filterYear && !filterMonth) { // if only year chosen show most recent month of that year
            const firstOfYear = reports.find(r => r.year === parseInt(filterYear));
            if (firstOfYear) { targetYear = firstOfYear.year; targetMonth = firstOfYear.month; }
        } else if (!filterYear && filterMonth) { // month chosen without year -> use most recent year having that month
            const firstWithMonth = reports.find(r => r.month === parseInt(filterMonth));
            if (firstWithMonth) { targetYear = firstWithMonth.year; targetMonth = firstWithMonth.month; }
        }
        return reports.filter(r => r.year === targetYear && r.month === targetMonth)
            .sort((a, b) => {
                const nameA = (propertyNameMap[a.propertyId] || '').toString().toLowerCase();
                const nameB = (propertyNameMap[b.propertyId] || '').toString().toLowerCase();
                return nameA.localeCompare(nameB);
            });
    }, [reports, propertyId, filterMonth, filterYear, propertyNameMap]);

    // Resolved period currently shown in the all-properties table
    const tablePeriod = useMemo(() => {
        if (propertyId || !reports.length) return null;
        const mostRecent = reports[0];
        let year = mostRecent.year;
        let month = mostRecent.month;
        if (filterYear && filterMonth) { year = parseInt(filterYear); month = parseInt(filterMonth); }
        else if (filterYear && !filterMonth) {
            const r = reports.find(r => r.year === parseInt(filterYear));
            if (r) { year = r.year; month = r.month; }
        } else if (!filterYear && filterMonth) {
            const r = reports.find(r => r.month === parseInt(filterMonth));
            if (r) { year = r.year; month = r.month; }
        }
        return { year, month };
    }, [reports, propertyId, filterYear, filterMonth]);

    // All accessible properties joined with their report for the period (null when no report exists)
    const tableRows = useMemo(() => {
        if (propertyId) {
            return tableReports.map(r => ({ report: r, pid: r.propertyId, name: propertyNameMap[r.propertyId] || String(r.propertyId) }));
        }
        // Fall back to report-only rows while properties are still loading
        if (!properties.length) {
            return tableReports.map(r => ({ report: r, pid: r.propertyId, name: propertyNameMap[r.propertyId] || String(r.propertyId) }));
        }
        const reportByPid = new Map(tableReports.map(r => [r.propertyId, r]));
        return properties
            .map(p => {
                const pid = p.propertyID || p.PropertyID;
                return { report: reportByPid.get(pid) || null, pid, name: p.propertyName || p.PropertyName || String(pid) };
            })
            .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
    }, [propertyId, tableReports, properties, propertyNameMap]);

  // Compute score change vs previous month for each table report
  const scoreChangeMap = useMemo(() => {
    const map = {};
    tableReports.forEach(r => {
      const currentScores = scoreMap[r.id];
      if (!currentScores || currentScores.total == null) return;
      // Find previous month
      let prevYear = r.month === 1 ? r.year - 1 : r.year;
      let prevMonth = r.month === 1 ? 12 : r.month - 1;
      const prevReport = reports.find(pr => pr.propertyId === r.propertyId && pr.year === prevYear && pr.month === prevMonth);
      if (!prevReport) return;
      const prevScores = scoreMap[prevReport.id];
      if (!prevScores || prevScores.total == null) return;
      const delta = currentScores.total - prevScores.total;
      map[r.id] = { delta, prevReportId: prevReport.id, currentReportId: r.id };
    });
    return map;
  }, [tableReports, reports, scoreMap]);

  // ---------------- Chart Data (trailing 12 months from selected period) ----------------
  const { chartData, currentYear, previousYear, isOverall } = useMemo(() => {
    const monthLabels = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    // Determine the anchor month: use the most recent report in the table, or fallback to now
    let anchorYear, anchorMonth;
    if (tableReports.length) {
      anchorYear = tableReports[0].year;
      anchorMonth = tableReports[0].month;
    } else {
      const now = new Date();
      anchorYear = now.getFullYear();
      anchorMonth = now.getMonth() + 1;
    }

    // Build trailing 12 months ending at anchor (inclusive)
    const startDate = new Date(anchorYear, anchorMonth - 12, 1);
    const trailing = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
      trailing.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }

    // The two calendar years that the trailing window spans
    const yearsInWindow = [...new Set(trailing.map(t => t.year))].sort((a, b) => a - b);
    const prevYearVal = yearsInWindow[0];
    const currYearVal = yearsInWindow.length > 1 ? yearsInWindow[yearsInWindow.length - 1] : prevYearVal;

    // Filter reports: all properties when no property selected, single property when selected
    const filteredReports = propertyId ? reports.filter(r => r.propertyId === parseInt(propertyId)) : reports;

    // Index reports by year-month for quick lookup
    const reportsByPeriod = new Map();
    filteredReports.forEach(r => {
      const key = `${r.year}-${r.month}`;
      if (!reportsByPeriod.has(key)) reportsByPeriod.set(key, []);
      reportsByPeriod.get(key).push(r);
    });

    // Compute independent per-year cumulative averages (resets at the year boundary)
    let prevCumSum = 0, prevCumCount = 0;
    let currCumSum = 0, currCumCount = 0;

    const data = trailing.map(t => {
      const key = `${t.year}-${t.month}`;
      const periodReports = reportsByPeriod.get(key) || [];
      const totals = periodReports.map(r => scoreMap[r.id]?.total).filter(v => v != null);
      const monthAvg = totals.length ? totals.reduce((a, b) => a + b, 0) / totals.length : null;

      let prevValue = null, currValue = null;

      if (t.year === prevYearVal && prevYearVal !== currYearVal) {
        if (monthAvg != null) { prevCumSum += monthAvg; prevCumCount++; }
        prevValue = prevCumCount > 0 ? parseFloat((prevCumSum / prevCumCount).toFixed(2)) : null;
      } else {
        if (monthAvg != null) { currCumSum += monthAvg; currCumCount++; }
        currValue = currCumCount > 0 ? parseFloat((currCumSum / currCumCount).toFixed(2)) : null;
      }

      return { monthLabel: `${monthLabels[t.month - 1]} ${t.year}`, previous: prevValue, current: currValue };
    });

    // Bridge: duplicate last previous-year value into the first current-year point for visual line continuity
    const boundaryIdx = trailing.findIndex(t => t.year === currYearVal);
    if (boundaryIdx > 0 && data[boundaryIdx - 1].previous != null) {
      data[boundaryIdx] = { ...data[boundaryIdx], previous: data[boundaryIdx - 1].previous };
    }

    return { chartData: data, currentYear: currYearVal, previousYear: prevYearVal, isOverall: !propertyId };
  }, [reports, scoreMap, propertyId, tableReports]);

  // Available years for dropdown (from fetched reports)
  const availableYears = useMemo(() => {
    const set = new Set(); reports.forEach(r=> set.add(r.year)); return Array.from(set).sort((a,b)=> b-a); }, [reports]);

    // NEW: table averages footer (only when no property selected)
    const tableAverages = useMemo(() => {
        if (propertyId || !tableRows.length) return null;
        let gaSum = 0, gaCount = 0, ocSum = 0, ocCount = 0, totSum = 0, totCount = 0;
        tableRows.forEach(({ report: r }) => {
            if (!r) return; // exclude no-report rows from the average
            const s = scoreMap[r.id];
            if (!s) return;
            if (s.ga != null) { gaSum += s.ga; gaCount++; }
            if (s.oc != null) { ocSum += s.oc; ocCount++; }
            if (s.total != null) { totSum += s.total; totCount++; }
        });
        if (!gaCount && !ocCount && !totCount) return null;
        return { ga: gaCount ? gaSum / gaCount : null, oc: ocCount ? ocSum / ocCount : null, total: totCount ? totSum / totCount : null };
    }, [propertyId, tableRows, scoreMap]);

  // Change Period year options: current year and 2 previous years
  const changePeriodYearOptions = useMemo(() => {
    const now = new Date();
    const cur = now.getFullYear();
    return [cur, cur - 1, cur - 2];
  }, []);

  const openChangePeriodModal = (report, e) => {
    e.stopPropagation();
    setChangePeriodReport(report);
    setChangePeriodMonth(String(report.month));
    setChangePeriodYear(String(report.year));
    setChangePeriodError('');
  };

  const closeChangePeriodModal = () => {
    setChangePeriodReport(null);
    setChangePeriodMonth('');
    setChangePeriodYear('');
    setChangePeriodError('');
  };

  const submitChangePeriod = async () => {
    if (!changePeriodReport || !changePeriodMonth || !changePeriodYear) return;
    const newMonth = parseInt(changePeriodMonth);
    const newYear = parseInt(changePeriodYear);
    if (newMonth === changePeriodReport.month && newYear === changePeriodReport.year) {
      setChangePeriodError('The selected period is the same as the current period.');
      return;
    }
    setChangePeriodLoading(true);
    setChangePeriodError('');
    try {
      const res = await apiFetch(`/api/sipa/reports/${changePeriodReport.id}/change-period`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: newYear, month: newMonth })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        closeChangePeriodModal();
        await search();
      } else {
        setChangePeriodError(data.message || 'Failed to change period.');
      }
    } catch (error) {
      Sentry.captureException(error);
      setChangePeriodError('An unexpected error occurred.');
    } finally {
      setChangePeriodLoading(false);
    }
  };



  return (
    <div className="sipa-page">
      <div className="sipa-header-row">
        <h1 className="section-title">SIPA Reports</h1>
        {loading && <span className="sipa-status-text">Loading...</span>}
      </div>
      <div className="sipa-controls-bar" style={{display:'flex', gap:12, flexWrap:'wrap'}}>
        <select value={propertyId} onChange={e=>setPropertyId(e.target.value)} style={{minWidth:200}}>
          <option value="">-- All Properties --</option>
          {properties.map(p=> <option key={p.propertyID || p.PropertyID} value={p.propertyID || p.PropertyID}>{p.propertyName || p.PropertyName}</option>)}
        </select>
        {!propertyId && (
          <>
            <select value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={{minWidth:160}}>
              <option value="">Month (Auto Recent)</option>
              {monthNames.map((m,i)=>(<option key={i+1} value={i+1}>{m}</option>))}
            </select>
            <select value={filterYear} onChange={e=>setFilterYear(e.target.value)} style={{minWidth:140}}>
              <option value="">Year (Auto Recent)</option>
              {availableYears.map(y=> <option key={y} value={y}>{y}</option>)}
            </select>
          </>
        )}
        {propertyHasNoReports && (
          <>
            <span style={{fontSize:'0.85rem', color:'#555', alignSelf:'center'}}>Start period:</span>
            <select value={startMonth} onChange={e=>setStartMonth(e.target.value)} style={{minWidth:140}}>
              <option value="">-- Month --</option>
              {monthNames.map((m,i)=>(<option key={i+1} value={i+1}>{m}</option>))}
            </select>
            <select value={startYear} onChange={e=>setStartYear(e.target.value)} style={{minWidth:100}}>
              <option value="">-- Year --</option>
              {startYearOptions.map(y=> <option key={y} value={y}>{y}</option>)}
            </select>
          </>
        )}
        <button className="button-secondary" onClick={openOrCreateCurrent} disabled={!propertyId || (propertyHasNoReports && (!startMonth || !startYear))} style={{opacity: (propertyId && !(propertyHasNoReports && (!startMonth || !startYear)))?1:0.5, cursor: (propertyId && !(propertyHasNoReports && (!startMonth || !startYear)))? 'pointer':'not-allowed'}}>{propertyHasNoReports ? 'Start New SIPA' : 'Open / Complete Current SIPA'}</button>
      </div>
      <div className="dashboard-card" style={{padding:'12px 14px'}}>
        <table>
          <thead>
            <tr>
              <th>Property</th>
              <th>Period</th>
              <th>General Appearance</th>
              <th>Operations & Compliance</th>
              <th>Total</th>
              <th>Submission Status</th>
              {canOverrideValidation && <th style={{width:'1%', whiteSpace:'nowrap'}}></th>}
            </tr>
          </thead>
          <tbody>
                      {tableRows.map(({ report: r, pid, name: propName }) => {
                          const scores = r
                              ? (scoreMap[r.id] || { ga: null, oc: null, total: null })
                              : { ga: 0, oc: 0, total: 0 };
                          const period = r ? { year: r.year, month: r.month } : tablePeriod;
                          const rowKey = r ? r.id : `no-report-${pid}`;
                          return (
                              <tr key={rowKey} style={{ cursor: r ? 'pointer' : 'default' }} onClick={r ? () => openReport(r) : undefined}>
                                  <td>{propName}</td>
                                  <td>{period ? formatPeriod(period.year, period.month) : '-'}</td>
                                  <td>{scores.ga != null ? scores.ga.toFixed(2) + '%' : '-'}</td>
                                  <td>{scores.oc != null ? scores.oc.toFixed(2) + '%' : '-'}</td>
                                  <td style={{ fontWeight: 600 }}>
                                      {scores.total != null ? scores.total.toFixed(2) + '%' : '-'}
                                      {r && scoreChangeMap[r.id] && (
                                          <span
                                              className={`sipa-score-delta ${scoreChangeMap[r.id].delta >= 0 ? 'improved' : 'declined'}`}
                                              title={`${scoreChangeMap[r.id].delta >= 0 ? '+' : ''}${scoreChangeMap[r.id].delta.toFixed(2)}% vs previous month — click to view comparison`}
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  const change = scoreChangeMap[r.id];
                                                  navigate(`/reporting/sipa-reporting?older=${change.prevReportId}&newer=${change.currentReportId}`);
                                              }}
                                          >
                                              {scoreChangeMap[r.id].delta >= 0 ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                                              {scoreChangeMap[r.id].delta >= 0 ? '+' : ''}{scoreChangeMap[r.id].delta.toFixed(2)}%
                                          </span>
                                      )}
                                  </td>
                                  <td>{r ? submissionStatus(r) : 'Not Started'}</td>
                                  {canOverrideValidation && (
                                      <td>
                                          {r && (
                                              <button
                                                  onClick={(e) => openChangePeriodModal(r, e)}
                                                  title="Change the period (month/year) for this report"
                                                  style={{ padding: '3px 8px', fontSize: '0.7rem', background: '#4169e1', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                              >
                                                  Change Period
                                              </button>
                                          )}
                                      </td>
                                  )}
                              </tr>
                          );
                      })}
                      {!tableRows.length && !loading && (
                          <tr><td colSpan={canOverrideValidation ? 7 : 6} style={{ textAlign: 'center' }}>No reports</td></tr>
                      )}
          </tbody>
          {tableAverages && (
            <tfoot>
              <tr style={{background:'#57565B', color:'#fff', fontWeight:600}}>
                <td colSpan={2} style={{textAlign:'right'}}>Averages:</td>
                <td>{tableAverages.ga!=null? tableAverages.ga.toFixed(2)+'%' : '-'}</td>
                <td>{tableAverages.oc!=null? tableAverages.oc.toFixed(2)+'%' : '-'}</td>
                <td style={{fontWeight:700}}>{tableAverages.total!=null? tableAverages.total.toFixed(2)+'%' : '-'}</td>
                <td></td>
                {canOverrideValidation && <td></td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {chartData.length > 0 && <SipaChart chartData={chartData} currentYear={currentYear} previousYear={previousYear} isOverall={isOverall} />}

      {/* Change Period Modal */}
      {changePeriodReport && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }} onClick={closeChangePeriodModal}>
          <div style={{ background:'#fff', borderRadius:8, padding:'24px 28px', minWidth:360, maxWidth:460, boxShadow:'0 8px 32px rgba(0,0,0,0.25)' }} onClick={(e)=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 4px 0', fontSize:'1rem' }}>Change Report Period</h3>
            <p style={{ margin:'0 0 16px 0', fontSize:'0.8rem', color:'#666' }}>
              {propertyNameMap[changePeriodReport.propertyId] || changePeriodReport.propertyId} — currently: <strong>{formatPeriod(changePeriodReport.year, changePeriodReport.month)}</strong>
            </p>
            <div style={{ display:'flex', gap:12, marginBottom:16 }}>
              <div style={{ flex:1 }}>
                <label style={{ display:'block', fontSize:'0.75rem', fontWeight:600, marginBottom:4 }}>Month</label>
                <select value={changePeriodMonth} onChange={(e)=>setChangePeriodMonth(e.target.value)} style={{ width:'100%', padding:'6px 8px' }}>
                  <option value="">-- Select --</option>
                  {monthNames.map((m,i)=>(<option key={i+1} value={i+1}>{m}</option>))}
                </select>
              </div>
              <div style={{ flex:1 }}>
                <label style={{ display:'block', fontSize:'0.75rem', fontWeight:600, marginBottom:4 }}>Year</label>
                <select value={changePeriodYear} onChange={(e)=>setChangePeriodYear(e.target.value)} style={{ width:'100%', padding:'6px 8px' }}>
                  <option value="">-- Select --</option>
                  {changePeriodYearOptions.map(y=> <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            {changePeriodError && (
              <div style={{ background:'#fee2e2', color:'#991b1b', padding:'8px 12px', borderRadius:4, fontSize:'0.8rem', marginBottom:12 }}>
                {changePeriodError}
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={closeChangePeriodModal} style={{ padding:'6px 16px', fontSize:'0.8rem', background:'#e5e7eb', color:'#333', border:'none', borderRadius:4, cursor:'pointer' }}>Cancel</button>
              <button onClick={submitChangePeriod} disabled={changePeriodLoading || !changePeriodMonth || !changePeriodYear} style={{ padding:'6px 16px', fontSize:'0.8rem', background:'#4169e1', color:'#fff', border:'none', borderRadius:4, cursor: (changePeriodLoading || !changePeriodMonth || !changePeriodYear) ? 'not-allowed' : 'pointer', opacity: (changePeriodLoading || !changePeriodMonth || !changePeriodYear) ? 0.6 : 1 }}>
                {changePeriodLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SipaReportListPage;